/**
 * API Client
 *
 * Axios instance configured with authentication interceptor.
 * Automatically attaches Bearer token to all requests.
 * On 401, silently refreshes the token and retries once.
 * Only triggers session-expired logout when the refresh itself fails.
 */

import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { toast } from "sonner";

/**
 * Thrown on unrecoverable 401 responses so React Query can identify
 * auth failures and skip retries.
 */
export class AuthenticationError extends Error {
  status = 401;
  constructor(message = "Authentication failed") {
    super(message);
    this.name = "AuthenticationError";
  }
}

const API_BASE_URL =
  import.meta.env.VITE_BACKEND_URL || "http://localhost:8101";

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

let getAccessToken: (() => Promise<string>) | null = null;
let onSessionExpired: (() => void) | null = null;

export function setTokenGetter(getter: () => Promise<string>) {
  getAccessToken = getter;
}

/**
 * Register a callback invoked when token refresh fails, meaning
 * the Auth0 session is truly expired/revoked and logout is appropriate.
 */
export function setOnSessionExpired(callback: () => void) {
  onSessionExpired = callback;
}

export function clearOnSessionExpired() {
  onSessionExpired = null;
}

/**
 * Get the current access token for direct fetch calls (e.g., streaming)
 */
export async function getCurrentAccessToken(): Promise<string | null> {
  if (getAccessToken) {
    try {
      return await getAccessToken();
    } catch (error) {
      console.error("Failed to get access token:", error);
      return null;
    }
  }
  return null;
}

apiClient.interceptors.request.use(
  async (config) => {
    if (getAccessToken) {
      try {
        const token = await getAccessToken();
        config.headers.Authorization = `Bearer ${token}`;
      } catch (error) {
        console.error("Failed to get access token:", error);
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retried?: boolean;
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetryableRequestConfig | undefined;

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retried
    ) {
      originalRequest._retried = true;

      if (getAccessToken) {
        try {
          const newToken = await getAccessToken();
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return apiClient(originalRequest);
        } catch {
          // Token refresh failed — session is truly expired
          if (onSessionExpired) {
            onSessionExpired();
          }
          return Promise.reject(new AuthenticationError());
        }
      }
    }

    // Already-retried 401 or no token getter — reject with AuthenticationError
    // so React Query knows not to retry.
    if (error.response?.status === 401) {
      return Promise.reject(new AuthenticationError());
    }

    if (error.response?.status === 402) {
      const detail =
        (error.response?.data as { detail?: string })?.detail ||
        "Subscription required";
      toast.error(detail, {
        action: {
          label: "Subscribe",
          onClick: () => {
            window.location.href = "/account?tab=billing";
          },
        },
      });
    }

    return Promise.reject(error);
  }
);
